/**
 * CSV / Excel formula-injection guard — shared by every user-facing spreadsheet
 * exporter (participant-set-xlsx.ts and the schedule excel-export.ts).
 *
 * A spreadsheet app evaluates a cell as a formula when its text begins with
 * `=`, `+`, `-`, or `@` (and TAB / CR can smuggle a leading control char that
 * some parsers strip before evaluation). User-controlled free text — participant
 * names, group names, task names, slot labels — is trusted on the way in (only
 * `.trim()`, per the CLAUDE.md "Free-text inputs" rule) and could therefore
 * carry a value like `=HYPERLINK("http://evil","x")` or `=WEBSERVICE(...)` that
 * executes when the exported file is opened or shared downstream.
 *
 * Prefixing a single apostrophe forces the cell to text; Excel/LibreOffice do
 * not display the apostrophe, and a round-trip back through the XLSX importers
 * reads the literal string (not a formula), so the value is preserved verbatim.
 *
 * Lives in `shared/` so both the web exporter and the Node-safe participant-set
 * module can import the one source of truth. Numeric / Date / enum cells never
 * need this — only call it on strings derived from user input.
 */
export function escapeFormulaInjection(raw: string): string {
  if (!raw) return raw;
  const first = raw.charCodeAt(0);
  // '=' 61, '+' 43, '-' 45, '@' 64, TAB 9, CR 13
  if (first === 61 || first === 43 || first === 45 || first === 64 || first === 9 || first === 13) {
    return `'${raw}`;
  }
  return raw;
}
